import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useCanActAsManager } from './useCanActAsManager';
import { useBackgroundPause } from './useBackgroundPause';
import { useTabLeader } from './useTabLeader';
import { toast } from 'sonner';

// Message schema
const messageSchema = z.object({
  id: z.string(),
  contact_id: z.string().nullable(),
  body: z.string().nullable(),
  direction: z.string().nullable(),
  status: z.string().nullable().optional(),
  error_code: z.string().nullable().optional(),
  owner_session_id: z.string().nullable(),
  sent_by_app_user_id: z.string().nullable(),
  created_at: z.string().nullable(),
  read_at: z.string().nullable(),
});

export type WhatsAppMessage = z.infer<typeof messageSchema>;

// Conversation (grouped by contact)
export interface Conversation {
  contactId: string;
  contactName: string;
  contactPhone: string;
  contactAvatar?: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  ownerSessionId: string;
}

// Fetch conversations for the current user (seller view)
async function fetchMyConversations(appUserId: string): Promise<Conversation[]> {
  const { data: messages, error } = await supabase
    .from('messages')
    .select('id, contact_id, body, direction, owner_session_id, created_at, read_at')
    .eq('owner_session_id', appUserId)
    .order('created_at', { ascending: false })
    .limit(500);

  if (error) throw error;

  // Get unique contact IDs
  const contactIds = [...new Set((messages || []).map(m => m.contact_id).filter(Boolean))] as string[];
  
  // Fetch contacts with correct column names
  const { data: contacts } = await supabase
    .from('contacts')
    .select('id, first_name, last_name, primary_phone')
    .in('id', contactIds);

  const contactMap = new Map(contacts?.map(c => [c.id, c]) ?? []);

  // Group by contact
  const conversationMap = new Map<string, Conversation>();

  for (const msg of messages || []) {
    if (!msg.contact_id) continue;

    const contact = contactMap.get(msg.contact_id);
    const contactName = contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown' : 'Unknown';

    if (!conversationMap.has(msg.contact_id)) {
      conversationMap.set(msg.contact_id, {
        contactId: msg.contact_id,
        contactName,
        contactPhone: contact?.primary_phone || '',
        contactAvatar: undefined,
        lastMessage: msg.body || '',
        lastMessageAt: msg.created_at || '',
        unreadCount: 0,
        ownerSessionId: msg.owner_session_id || appUserId,
      });
    }

    // Count unread messages (inbound, not read)
    if (msg.direction === 'inbound' && !msg.read_at) {
      const conv = conversationMap.get(msg.contact_id)!;
      conv.unreadCount++;
    }
  }

  // Sort by last message time
  return Array.from(conversationMap.values()).sort(
    (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
  );
}

// Fetch messages for a specific contact
async function fetchMessagesForContact(
  contactId: string,
  ownerSessionId?: string
): Promise<WhatsAppMessage[]> {
  let query = supabase
    .from('messages')
    .select('*')
    .eq('contact_id', contactId)
    .order('created_at', { ascending: true })
    .limit(100);

  if (ownerSessionId) {
    query = query.eq('owner_session_id', ownerSessionId);
  }

  const { data, error } = await query;

  if (error) throw error;

  const parsed = z.array(messageSchema).safeParse(data);
  if (!parsed.success) {
    console.error('Invalid messages data:', parsed.error.flatten());
    return [];
  }

  return parsed.data;
}

export function useWhatsAppInbox() {
  const { user } = useAuth();
  const { appUserId } = useCanActAsManager();
  const queryClient = useQueryClient();
  const { isBackground } = useBackgroundPause();
  const { isLeader } = useTabLeader('whatsapp-inbox-leader');

  // Query conversations for current user
  // Only poll if leader tab AND tab is visible
  const conversationsQuery = useQuery({
    queryKey: ['whatsAppConversations', appUserId],
    queryFn: () => fetchMyConversations(appUserId!),
    enabled: !!appUserId,
    staleTime: 1000 * 10,
    refetchInterval: isLeader && !isBackground ? 1000 * 30 : false, // Auto-refresh every 30s only if leader and visible
  });

  // Mark messages as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (contactId: string) => {
      const { error } = await supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .eq('contact_id', contactId)
        .eq('owner_session_id', appUserId)
        .eq('direction', 'inbound')
        .is('read_at', null);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsAppConversations'] });
    },
  });

  // Subscribe to new messages with Realtime
  const subscribeToMessages = (callback: (message: WhatsAppMessage) => void) => {
    if (!appUserId) return () => {};

    const channel = supabase
      .channel('whatsapp-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `owner_session_id=eq.${appUserId}`,
        },
        (payload) => {
          const parsed = messageSchema.safeParse(payload.new);
          if (parsed.success) {
            callback(parsed.data);
            // Invalidate conversations to update list
            queryClient.invalidateQueries({ queryKey: ['whatsAppConversations'] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  return {
    // Conversations list
    conversations: conversationsQuery.data ?? [],
    conversationsLoading: conversationsQuery.isLoading,
    conversationsError: conversationsQuery.error,
    refetchConversations: conversationsQuery.refetch,

    // Messages for a specific contact (use this in chat view)
    fetchMessagesForContact,

    // Mark as read
    markAsRead: markAsReadMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,

    // Realtime subscription
    subscribeToMessages,

    // Current user info
    appUserId,
  };
}
